import { createFileRoute, Link } from "@tanstack/react-router";
import React, { useMemo, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  AlertCircle,
  FilterX,
  Loader2,
  Lock,
  Plus,
  RefreshCw,
  Search,
  Users,
} from "lucide-react";
import { format } from "date-fns";
import { useOrganization } from "@/hooks/use-organization";
import { useTRPC } from "@/trpc";
import { useQuery } from "@tanstack/react-query";
import { ProviderPoolEditDialog } from "@/features/provider-pool/provider-pool-edit-dialog";
import { useRolePermissions, Permission } from "@/hooks/use-role-permissions";
import { useOrgPath } from "@/hooks/useOrgPath";

export const Route = createFileRoute(
  "/_org/org/bu/$organizationBusinessUnitId/providers/pools/",
)({
  component: ProviderPoolsPage,
});

function ProviderPoolsPage() {
  const { user } = Route.useRouteContext();
  const { hasPermission } = useRolePermissions(user);
  const { selectedOrgBusinessUnit } = useOrganization();
  const trpc = useTRPC();
  const orgPath = useOrgPath();
  const [searchTerm, setSearchTerm] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedPoolId, setSelectedPoolId] = useState<string | undefined>();

  const canView = hasPermission(Permission.ViewProviderPools);
  const canManage = hasPermission(Permission.ManageProviderPools);

  const {
    data: pools,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery(
    trpc.providerPool.list.queryOptions({
      organizationBusinessUnitId: selectedOrgBusinessUnit.id,
    }),
  );

  const filteredPools = useMemo(() => {
    if (!pools) {
      return [];
    }
    const query = searchTerm.trim().toLowerCase();
    if (!query) {
      return pools;
    }
    return pools.filter((pool) => pool.name.toLowerCase().includes(query));
  }, [pools, searchTerm]);

  const hasFilters = searchTerm.trim() !== "";

  const handleCreatePool = () => {
    setSelectedPoolId(undefined);
    setDialogOpen(true);
  };

  const handleEditPool = (poolId: string) => {
    setSelectedPoolId(poolId);
    setDialogOpen(true);
  };

  const handleResetFilters = () => {
    setSearchTerm("");
  };

  if (!canView) {
    return (
      <div className="min-h-screen bg-slate-50">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-8 py-4 sm:py-8">
          <Card className="rounded-2xl border-dashed border-slate-300 bg-white shadow-xs">
            <CardContent className="p-8 md:p-12 flex flex-col items-center justify-center text-center gap-4">
              <div className="bg-slate-100 p-3 rounded-full">
                <Lock className="h-8 w-8 text-slate-600" />
              </div>
              <div>
                <h3 className="text-xl font-semibold text-slate-900 mb-1">
                  Access Restricted
                </h3>
                <p className="text-slate-600 max-w-md">
                  You don't have permission to view provider pools. Contact your
                  administrator to request access.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-8 py-4 sm:py-8 space-y-4 sm:space-y-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">
              Provider pools
            </h1>
            <p className="mt-1 text-slate-600 text-sm sm:text-base">
              Group providers into actionable cohorts to streamline outreach and
              staffing.
            </p>
          </div>
          {canManage && (
            <div className="flex shrink-0 flex-col sm:flex-row gap-2 w-full sm:w-auto">
              <Button
                className="flex items-center gap-2 w-full sm:w-auto"
                onClick={handleCreatePool}
              >
                <Plus size={16} />
                Add pool
              </Button>
            </div>
          )}
        </div>

        <div className="space-y-3">
          <div className="grid min-w-0 gap-2">
            <Label htmlFor="provider-pools-search">Search</Label>
            <div className="relative">
              <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                <Search aria-hidden="true" className="h-4 w-4 text-slate-400" />
              </div>
              <Input
                id="provider-pools-search"
                placeholder="Search provider pools..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>

          {hasFilters && (
            <div className="flex flex-wrap items-center gap-2 text-xs text-slate-600">
              <span className="inline-flex items-center gap-1 font-semibold uppercase tracking-wide text-slate-500">
                <FilterX className="h-3.5 w-3.5" />
                Active filters
              </span>
              {searchTerm && (
                <span className="rounded-full border border-primary/20 bg-primary/5 px-3 py-1 text-primary [overflow-wrap:anywhere]">
                  Search: {searchTerm}
                </span>
              )}
              <Button
                variant="ghost"
                size="sm"
                className="h-7 px-3 text-slate-500 hover:text-slate-700"
                onClick={handleResetFilters}
              >
                <FilterX className="mr-1 h-3.5 w-3.5" />
                Clear all
              </Button>
            </div>
          )}
        </div>

        {isLoading ? (
          <LoadingState />
        ) : isError ? (
          <ErrorState
            message={error instanceof Error ? error.message : undefined}
            onRetry={refetch}
          />
        ) : filteredPools.length > 0 ? (
          <PoolsTable
            pools={filteredPools}
            onEdit={handleEditPool}
            canManage={canManage}
            orgPath={orgPath}
          />
        ) : (
          <EmptyState
            hasFilters={hasFilters}
            onCreate={handleCreatePool}
            onReset={handleResetFilters}
            canManage={canManage}
          />
        )}

        <ProviderPoolEditDialog
          poolId={selectedPoolId}
          open={dialogOpen}
          onOpenChange={setDialogOpen}
          onSuccess={() => {
            setSelectedPoolId(undefined);
            setDialogOpen(false);
          }}
        />
      </div>
    </div>
  );
}

function LoadingState() {
  return (
    <Card className="rounded-2xl border-dashed border-slate-300 bg-white shadow-xs">
      <CardContent className="p-8 md:p-12 flex flex-col items-center justify-center text-center">
        <div className="bg-primary/10 p-3 rounded-full mb-4">
          <Loader2 className="h-8 w-8 text-primary animate-spin" />
        </div>
        <h3 className="text-xl font-semibold mb-2">Loading provider pools</h3>
        <p className="text-slate-600 mb-6 max-w-md">
          Please wait while we fetch the latest provider pools for your
          organization.
        </p>
      </CardContent>
    </Card>
  );
}

function ErrorState({
  message,
  onRetry,
}: {
  message?: string;
  onRetry: () => void;
}) {
  return (
    <Card className="rounded-2xl border-dashed border-2 border-red-300 bg-red-50 shadow-xs">
      <CardContent className="p-8 md:p-12 flex flex-col items-center justify-center text-center gap-4">
        <div className="bg-red-100 p-3 rounded-full">
          <AlertCircle className="h-8 w-8 text-red-600" />
        </div>
        <div>
          <h3 className="text-xl font-semibold text-red-700 mb-1">
            Failed to load provider pools
          </h3>
          <p className="text-slate-600 max-w-md">
            {message ??
              "An error occurred while loading provider pools. Please try again."}
          </p>
        </div>
        <Button onClick={onRetry}>
          <RefreshCw className="mr-2 h-4 w-4" />
          Retry
        </Button>
      </CardContent>
    </Card>
  );
}

function PoolsTable({
  pools,
  onEdit,
  canManage,
  orgPath,
}: {
  pools: Array<{
    id: string;
    name: string;
    memberCount: number;
    createdAt: string;
  }>;
  onEdit: (poolId: string) => void;
  canManage: boolean;
  orgPath: (subPath: string) => string;
}) {
  return (
    <Card className="p-0 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-xs">
      <Table>
        <TableHeader>
          <TableRow className="bg-slate-50 text-slate-600 uppercase tracking-wide text-xs">
            <TableHead className="w-12" />
            <TableHead>Pool name</TableHead>
            <TableHead className="text-center">Members</TableHead>
            <TableHead>Created</TableHead>
            {canManage && <TableHead className="text-right">Actions</TableHead>}
          </TableRow>
        </TableHeader>
        <TableBody>
          {pools.map((pool) => (
            <TableRow key={pool.id} className="hover:bg-slate-50">
              <TableCell className="py-3">
                <Avatar className="h-10 w-10">
                  <AvatarFallback className="bg-primary/10">
                    <Users className="h-5 w-5 text-primary" />
                  </AvatarFallback>
                </Avatar>
              </TableCell>
              <TableCell className="py-3">
                <Link
                  to={orgPath("/providers/pools/" + pool.id)}
                  className="text-primary hover:text-primary/80 font-semibold"
                >
                  {pool.name}
                </Link>
              </TableCell>
              <TableCell className="py-3 text-center text-sm font-medium text-slate-700">
                {pool.memberCount} provider{pool.memberCount === 1 ? "" : "s"}
              </TableCell>
              <TableCell className="py-3 text-sm text-slate-600">
                {format(new Date(pool.createdAt), "MMM d, yyyy")}
              </TableCell>
              {canManage && (
                <TableCell className="py-3 text-right">
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-primary hover:text-primary/80"
                    onClick={() => onEdit(pool.id)}
                  >
                    Edit
                  </Button>
                </TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Card>
  );
}

function EmptyState({
  hasFilters,
  onCreate,
  onReset,
  canManage,
}: {
  hasFilters: boolean;
  onCreate: () => void;
  onReset: () => void;
  canManage: boolean;
}) {
  return (
    <Card className="rounded-2xl border-dashed border-slate-300 bg-white shadow-xs">
      <CardContent className="p-8 md:p-12 flex flex-col items-center justify-center text-center gap-4">
        <div className="bg-primary/10 p-3 rounded-full">
          <Users className="h-8 w-8 text-primary" />
        </div>
        <div>
          <h3 className="text-xl font-semibold text-slate-900 mb-1">
            No provider pools found
          </h3>
          <p className="text-slate-600 max-w-md">
            {hasFilters
              ? "No pools match your current search. Adjust your filters to see more results."
              : "Create a pool to group providers for quick outreach."}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          {hasFilters ? (
            <Button
              variant="outline"
              onClick={onReset}
              className="flex items-center gap-2"
            >
              <FilterX size={16} />
              Reset filters
            </Button>
          ) : canManage ? (
            <Button onClick={onCreate} className="flex items-center gap-2">
              <Plus size={16} />
              Create pool
            </Button>
          ) : null}
        </div>
      </CardContent>
    </Card>
  );
}
